import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Users,
  Plus,
  Search,
  Upload,
  FileDown,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Building,
  MoreVertical,
  RefreshCw,
  Check,
  X,
  AlertCircle,
  User,
  Globe,
  UserPlus,
  TrendingUp,
  Clock,
  FileText,
  Calendar,
  MessageSquare,
  PhoneCall,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AnnuaireClientsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]); // For bulk selection
  const [selectAll, setSelectAll] = useState(false);
  const fileInputRef = useRef(null);
  
  // Historique state
  const [showHistoriqueDialog, setShowHistoriqueDialog] = useState(false);
  const [selectedClientForHistorique, setSelectedClientForHistorique] = useState(null);
  const [historiqueData, setHistoriqueData] = useState([]);
  const [loadingHistorique, setLoadingHistorique] = useState(false);
  const [newHistoriqueEntry, setNewHistoriqueEntry] = useState({ type: "contact", description: "" });
  
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    entreprise: "",
    adresse: "",
    ville: "",
    code_postal: "",
    pays: "France",
    categorie: "particulier",
    notes: "",
    site_web: "",
  });

  // Categories
  const categories = [
    { value: "particulier", label: "Particulier" },
    { value: "entreprise", label: "Entreprise" },
    { value: "association", label: "Association" },
    { value: "collectivite", label: "Collectivité" },
    { value: "evenementiel", label: "Événementiel" },
  ];

  // Fetch clients
  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/annuaire/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchClients();
  }, [token]);

  // Filter clients
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = !searchTerm || 
        client.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.entreprise?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.ville?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.telephone?.includes(searchTerm);
      
      const matchesCategory = categoryFilter === "all" || client.categorie === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  }, [clients, searchTerm, categoryFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: clients.length,
    particuliers: clients.filter(c => c.categorie === "particulier").length,
    entreprises: clients.filter(c => c.categorie === "entreprise").length,
    autres: clients.filter(c => !["particulier", "entreprise"].includes(c.categorie)).length,
  }), [clients]);

  // Reset form
  const resetForm = () => {
    setFormData({
      nom: "",
      email: "",
      telephone: "",
      entreprise: "",
      adresse: "",
      ville: "",
      code_postal: "",
      pays: "France",
      categorie: "particulier",
      notes: "",
      site_web: "",
    });
    setEditingClient(null);
  };

  // Sync from CRM
  const handleSyncCRM = async () => {
    try {
      const response = await axios.post(`${API}/api/annuaire/sync-crm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchClients();
    } catch (error) {
      console.error("Error syncing CRM:", error);
      toast.error("Erreur lors de la synchronisation CRM");
    }
  };

  // Sync from Leads
  const handleSyncLeads = async () => {
    try {
      const response = await axios.post(`${API}/api/annuaire/sync-leads`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchClients();
    } catch (error) {
      console.error("Error syncing leads:", error);
      toast.error("Erreur lors de la synchronisation des leads");
    }
  };

  // Open edit dialog
  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      nom: client.nom || "",
      email: client.email || "",
      telephone: client.telephone || "",
      entreprise: client.entreprise || "",
      adresse: client.adresse || "",
      ville: client.ville || "",
      code_postal: client.code_postal || "",
      pays: client.pays || "France",
      categorie: client.categorie || "particulier",
      notes: client.notes || "",
      site_web: client.site_web || "",
    });
    setShowCreateDialog(true);
  };

  // Save client
  const handleSaveClient = async () => {
    try {
      // Validate required fields
      if (!formData.nom || !formData.nom.trim()) {
        toast.error("Le nom est obligatoire");
        return;
      }
      if (!formData.email || !formData.email.trim()) {
        toast.error("L'email est obligatoire");
        return;
      }
      if (!formData.telephone || !formData.telephone.trim()) {
        toast.error("Le téléphone est obligatoire");
        return;
      }
      if (!formData.entreprise || !formData.entreprise.trim()) {
        toast.error("L'entreprise/société est obligatoire");
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingClient) {
        await axios.put(`${API}/api/annuaire/clients/${editingClient.id}`, formData, { headers });
        toast.success("Client mis à jour");
      } else {
        await axios.post(`${API}/api/annuaire/clients`, formData, { headers });
        toast.success("Client créé");
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error("Error saving client:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  // Delete client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    
    try {
      await axios.delete(`${API}/api/annuaire/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client supprimé");
      fetchClients();
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Open historique dialog
  const openHistoriqueDialog = async (client) => {
    setSelectedClientForHistorique(client);
    setShowHistoriqueDialog(true);
    setLoadingHistorique(true);
    setNewHistoriqueEntry({ type: "contact", description: "" });
    
    try {
      const response = await axios.get(`${API}/api/annuaire/clients/${client.id}/historique`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoriqueData(response.data.historique || []);
    } catch (error) {
      console.error("Error fetching historique:", error);
      toast.error("Erreur lors du chargement de l'historique");
      setHistoriqueData([]);
    } finally {
      setLoadingHistorique(false);
    }
  };

  // Add new historique entry
  const handleAddHistoriqueEntry = async () => {
    if (!newHistoriqueEntry.description.trim()) {
      toast.error("La description est obligatoire");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/api/annuaire/clients/${selectedClientForHistorique.id}/historique`,
        newHistoriqueEntry,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add new entry to the list
      setHistoriqueData([response.data.entry, ...historiqueData]);
      setNewHistoriqueEntry({ type: "contact", description: "" });
      toast.success("Entrée ajoutée à l'historique");
    } catch (error) {
      console.error("Error adding historique:", error);
      toast.error("Erreur lors de l'ajout");
    }
  };

  // Get historique type icon
  const getHistoriqueTypeIcon = (type) => {
    switch (type) {
      case "contact": return <MessageSquare className="w-4 h-4" />;
      case "appel": return <PhoneCall className="w-4 h-4" />;
      case "email": return <Mail className="w-4 h-4" />;
      case "devis": return <FileText className="w-4 h-4" />;
      case "evenement": return <Calendar className="w-4 h-4" />;
      case "note": return <FileText className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Get historique type badge color
  const getHistoriqueTypeBadge = (type) => {
    const colors = {
      contact: "bg-blue-100 text-blue-700",
      appel: "bg-green-100 text-green-700",
      email: "bg-purple-100 text-purple-700",
      devis: "bg-amber-100 text-amber-700",
      evenement: "bg-pink-100 text-pink-700",
      note: "bg-gray-100 text-gray-700",
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  // Toggle select client
  const toggleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedClients.length === 0) {
      toast.error("Aucun client sélectionné");
      return;
    }
    
    if (!window.confirm(`Supprimer ${selectedClients.length} client(s) sélectionné(s) ?`)) return;
    
    try {
      await axios.post(`${API}/api/annuaire/clients/bulk-delete`, {
        client_ids: selectedClients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedClients.length} client(s) supprimé(s)`);
      setSelectedClients([]);
      setSelectAll(false);
      fetchClients();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Parse CSV file
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter(line => line.trim());
        const headers = lines[0].split(/[,;]/).map(h => h.trim().toLowerCase().replace(/"/g, ""));
        
        // Map headers to expected fields
        const fieldMap = {
          nom: ["nom", "name", "contact", "nom_contact"],
          email: ["email", "mail", "e-mail", "courriel"],
          telephone: ["telephone", "tel", "phone", "téléphone", "mobile"],
          entreprise: ["entreprise", "societe", "société", "company", "organisation"],
          adresse: ["adresse", "address", "rue"],
          ville: ["ville", "city", "commune"],
          code_postal: ["code_postal", "cp", "zip", "postal"],
          pays: ["pays", "country"],
          categorie: ["categorie", "category", "type"],
          notes: ["notes", "commentaire", "remarque"],
          site_web: ["site_web", "website", "site", "url"],
        };
        
        const headerIndices = {};
        Object.keys(fieldMap).forEach(field => {
          const index = headers.findIndex(h => fieldMap[field].includes(h));
          if (index !== -1) headerIndices[field] = index;
        });
        
        const data = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(/[,;]/).map(v => v.trim().replace(/"/g, ""));
          
          const row = { nom: "", email: "", telephone: "", entreprise: "", adresse: "", ville: "", code_postal: "", pays: "France", categorie: "particulier", notes: "", site_web: "" };
          
          Object.keys(headerIndices).forEach(field => {
            const value = values[headerIndices[field]];
            if (value) row[field] = value;
          });
          
          if (!row.nom && !row.email && !row.entreprise) {
            errors.push(`Ligne ${i + 1}: Données insuffisantes (nom, email ou entreprise requis)`);
          } else {
            data.push(row);
          }
        }
        
        setImportData(data);
        setImportErrors(errors);
        setShowImportDialog(true);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.error("Erreur lors de la lecture du fichier");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  // Import clients
  const handleImport = async () => {
    try {
      const response = await axios.post(`${API}/api/annuaire/clients/import`, {
        clients: importData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.imported || importData.length} client(s) importé(s)`);
      setShowImportDialog(false);
      setImportData([]);
      setImportErrors([]);
      fetchClients();
    } catch (error) {
      console.error("Error importing:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Nom", "Email", "Téléphone", "Entreprise", "Adresse", "Ville", "Code Postal", "Pays", "Catégorie", "Site Web", "Notes"];
    const rows = filteredClients.map(c => [
      c.nom || "",
      c.email || "",
      c.telephone || "",
      c.entreprise || "",
      c.adresse || "",
      c.ville || "",
      c.code_postal || "",
      c.pays || "",
      c.categorie || "",
      c.site_web || "",
      c.notes || ""
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `annuaire_clients_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    toast.success("Export CSV téléchargé");
  };

  // Get category badge
  const getCategoryBadge = (category) => {
    const config = {
      particulier: { label: "Particulier", className: "bg-blue-100 text-blue-700" },
      entreprise: { label: "Entreprise", className: "bg-green-100 text-green-700" },
      association: { label: "Association", className: "bg-purple-100 text-purple-700" },
      collectivite: { label: "Collectivité", className: "bg-amber-100 text-amber-700" },
      evenementiel: { label: "Événementiel", className: "bg-pink-100 text-pink-700" },
    };
    const c = config[category] || { label: category, className: "bg-gray-100 text-gray-700" };
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Annuaire Clients</h1>
          <p className="text-muted-foreground">Gérez vos contacts clients</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Bulk delete button - only show when items are selected */}
          {selectedClients.length > 0 && (
            <Button variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer ({selectedClients.length})
            </Button>
          )}
          <Button variant="outline" onClick={fetchClients}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button variant="outline" onClick={handleSyncCRM} className="border-green-300 text-green-700 hover:bg-green-50">
            <UserPlus className="w-4 h-4 mr-2" />
            Sync CRM
          </Button>
          <Button variant="outline" onClick={handleSyncLeads} className="border-purple-300 text-purple-700 hover:bg-purple-50">
            <TrendingUp className="w-4 h-4 mr-2" />
            Sync Leads
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" />
            Importer CSV
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.txt"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button variant="outline" onClick={exportToCSV}>
            <FileDown className="w-4 h-4 mr-2" />
            Exporter
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau client
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-600">{stats.particuliers}</p>
                <p className="text-xs text-muted-foreground">Particuliers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.entreprises}</p>
                <p className="text-xs text-muted-foreground">Entreprises</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{stats.autres}</p>
                <p className="text-xs text-muted-foreground">Autres</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email, entreprise, ville..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {filteredClients.length} client(s) trouvé(s)
          {selectedClients.length > 0 && (
            <span className="ml-2 text-primary font-medium">
              ({selectedClients.length} sélectionné{selectedClients.length > 1 ? "s" : ""})
            </span>
          )}
        </div>
        {filteredClients.length > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleSelectAll}
            className="text-sm"
          >
            {selectAll ? "Tout désélectionner" : "Tout sélectionner"}
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox 
                    checked={selectAll}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow 
                    key={client.id} 
                    className={`hover:bg-gray-50 ${selectedClients.includes(client.id) ? "bg-blue-50" : ""}`}
                  >
                    <TableCell>
                      <Checkbox 
                        checked={selectedClients.includes(client.id)}
                        onCheckedChange={() => toggleSelectClient(client.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {client.nom || "-"}
                    </TableCell>
                    <TableCell>
                      {client.email ? (
                        <a href={`mailto:${client.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </a>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {client.telephone ? (
                        <a href={`tel:${client.telephone}`} className="flex items-center gap-1 text-green-600 hover:underline">
                          <Phone className="w-3 h-3" />
                          {client.telephone}
                        </a>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {client.entreprise ? (
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3 text-gray-400" />
                          {client.entreprise}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {client.ville ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          {client.ville}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {getCategoryBadge(client.categorie)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(client)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openHistoriqueDialog(client)}>
                            <Clock className="w-4 h-4 mr-2" />
                            Voir historique
                          </DropdownMenuItem>
                          {client.email && (
                            <DropdownMenuItem asChild>
                              <a href={`mailto:${client.email}`}>
                                <Mail className="w-4 h-4 mr-2" />
                                Envoyer email
                              </a>
                            </DropdownMenuItem>
                          )}
                          {client.site_web && (
                            <DropdownMenuItem asChild>
                              <a href={client.site_web} target="_blank" rel="noopener noreferrer">
                                <Globe className="w-4 h-4 mr-2" />
                                Site web
                              </a>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeleteClient(client.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Modifier le client" : "Nouveau client"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Nom du contact"
                  required
                  className={!formData.nom ? "border-red-300" : ""}
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemple.com"
                  required
                  className={!formData.email ? "border-red-300" : ""}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="06 XX XX XX XX"
                  required
                  className={!formData.telephone ? "border-red-300" : ""}
                />
              </div>
              <div>
                <Label>Entreprise / Société *</Label>
                <Input
                  value={formData.entreprise}
                  onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                  placeholder="Nom de l'entreprise"
                  required
                  className={!formData.entreprise ? "border-red-300" : ""}
                />
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">* Champs obligatoires</p>
            
            <div>
              <Label>Adresse</Label>
              <Input
                value={formData.adresse}
                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                placeholder="Numéro et rue"
              />
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label>Ville</Label>
                <Input
                  value={formData.ville}
                  onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                  placeholder="Ville"
                />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input
                  value={formData.code_postal}
                  onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  placeholder="75000"
                />
              </div>
              <div>
                <Label>Pays</Label>
                <Input
                  value={formData.pays}
                  onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                  placeholder="France"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={(val) => setFormData({ ...formData, categorie: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site web</Label>
                <Input
                  value={formData.site_web}
                  onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes ou remarques..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveClient}>
              {editingClient ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importer des clients</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {importErrors.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-medium text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {importErrors.length} avertissement(s)
                </p>
                <ul className="text-sm text-amber-700 mt-2 space-y-1">
                  {importErrors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {importErrors.length > 5 && <li>... et {importErrors.length - 5} autres</li>}
                </ul>
              </div>
            )}
            
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-medium text-green-800 flex items-center gap-2">
                <Check className="w-4 h-4" />
                {importData.length} client(s) prêt(s) à importer
              </p>
            </div>
            
            {importData.length > 0 && (
              <div className="max-h-60 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Ville</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importData.slice(0, 10).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{row.nom || "-"}</TableCell>
                        <TableCell>{row.email || "-"}</TableCell>
                        <TableCell>{row.telephone || "-"}</TableCell>
                        <TableCell>{row.entreprise || "-"}</TableCell>
                        <TableCell>{row.ville || "-"}</TableCell>
                      </TableRow>
                    ))}
                    {importData.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          ... et {importData.length - 10} autres
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div className="text-sm text-muted-foreground">
              <p className="font-medium">Colonnes reconnues :</p>
              <p>nom, email, telephone, entreprise, adresse, ville, code_postal, pays, categorie, site_web, notes</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleImport} disabled={importData.length === 0}>
              <Upload className="w-4 h-4 mr-2" />
              Importer {importData.length} client(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historique Dialog */}
      <Dialog open={showHistoriqueDialog} onOpenChange={setShowHistoriqueDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Historique - {selectedClientForHistorique?.nom}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Client Info Summary */}
            {selectedClientForHistorique && (
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <div className="flex flex-wrap gap-4">
                  <span><strong>Entreprise:</strong> {selectedClientForHistorique.entreprise || "-"}</span>
                  <span><strong>Email:</strong> {selectedClientForHistorique.email || "-"}</span>
                  <span><strong>Tél:</strong> {selectedClientForHistorique.telephone || "-"}</span>
                </div>
              </div>
            )}
            
            {/* Add New Entry */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Ajouter une entrée</h4>
              <div className="flex gap-2">
                <Select 
                  value={newHistoriqueEntry.type} 
                  onValueChange={(v) => setNewHistoriqueEntry({...newHistoriqueEntry, type: v})}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contact">Contact</SelectItem>
                    <SelectItem value="appel">Appel</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="reunion">Réunion</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Description de l'interaction..."
                  value={newHistoriqueEntry.description}
                  onChange={(e) => setNewHistoriqueEntry({...newHistoriqueEntry, description: e.target.value})}
                  className="flex-1"
                />
                <Button onClick={handleAddHistoriqueEntry} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Historique List */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                Historique ({historiqueData.length} entrée{historiqueData.length > 1 ? "s" : ""})
              </h4>
              
              {loadingHistorique ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : historiqueData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Aucun historique pour ce client</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {historiqueData.map((entry, index) => (
                    <div 
                      key={entry.id || index} 
                      className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className={`p-2 rounded-full ${getHistoriqueTypeBadge(entry.type)}`}>
                        {getHistoriqueTypeIcon(entry.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs capitalize">
                            {entry.type}
                          </Badge>
                          {entry.source === "crm" && (
                            <Badge variant="secondary" className="text-xs">CRM</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {entry.date ? format(new Date(entry.date), "dd/MM/yyyy HH:mm", { locale: fr }) : "-"}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{entry.description}</p>
                        {entry.created_by_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Par: {entry.created_by_name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoriqueDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
